/**
 * <s-cart-gift> autosaves the cart page gift fields (Is this a gift?, From, To,
 * Gift Message) as cart attributes so they reach the order even when a buyer
 * leaves through an express checkout button instead of the form's Check out submit.
 *
 * Required markup:
 *   data-update-url  — Liquid `routes.cart_update_url`; `.js` is appended here per the AJAX API contract.
 *   data-error-message — Translated text shown when saving fails.
 *   Descendant inputs/textareas named `attributes[<key>]` — saved under <key>.
 *   [data-gift-error] — Hidden alert element for save failures.
 */
class CartGift extends HTMLElement {
  #timer = null;
  #ac = null;
  #lifecycleAc = null;

  connectedCallback() {
    this.#lifecycleAc = new AbortController();
    const opts = { signal: this.#lifecycleAc.signal };

    this.addEventListener('input', (event) => {
      if (!this.#isField(event.target)) return;
      clearTimeout(this.#timer);
      this.#timer = setTimeout(() => this.#save(), 400);
    }, opts);

    this.addEventListener('change', (event) => {
      if (!this.#isField(event.target)) return;
      clearTimeout(this.#timer);
      this.#save();
    }, opts);
  }

  disconnectedCallback() {
    this.#lifecycleAc?.abort();
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  #isField(target) {
    return target instanceof Element && target.matches('[name^="attributes["]');
  }

  #collect() {
    const attributes = {};
    for (const field of this.querySelectorAll('[name^="attributes["]')) {
      if (field.type === 'radio' && !field.checked) continue;
      const key = field.name.slice('attributes['.length, -1);
      attributes[key] = field.value;
    }
    return attributes;
  }

  async #save() {
    const url = this.dataset.updateUrl;
    if (!url) return;

    this.#ac?.abort();
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const error = this.querySelector('[data-gift-error]');

    try {
      const response = await fetch(`${url}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: this.#collect() }),
        signal,
      });
      if (!response.ok) throw new Error('Cart attribute save failed');
      if (error) error.hidden = true;
    } catch (err) {
      if (signal.aborted) return;
      if (error) {
        error.textContent = this.dataset.errorMessage || '';
        error.hidden = false;
      }
    }
  }
}

if (!customElements.get('s-cart-gift')) {
  customElements.define('s-cart-gift', CartGift);
}
